/**
 * URL validation utilities
 * Shared validation logic for URL format validation
 * @module validation/url
 */

/**
 * Validate URL format
 * @param url - URL to validate
 * @returns true if URL format is valid, false otherwise
 */
export function isValidUrl(url: string): boolean {
  if (!url) {
    return false;
  }

  const trimmedUrl = url.trim();

  // Check if it's a valid URL using native URL constructor
  try {
    const urlObj = new URL(trimmedUrl);
    // Only allow http and https protocols
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * URL validation result with detailed feedback
 */
export interface UrlValidationResult {
  /** Whether the URL is valid */
  isValid: boolean;
  /** Translation key for error message if invalid */
  errorKey?: string;
}

/**
 * Validate URL format with detailed result
 * Returns translation key for error messages
 * @param url - URL to validate
 * @returns Validation result with translation key for error if invalid
 */
export function validateUrl(url: string): UrlValidationResult {
  if (!url || url.trim().length === 0) {
    return {
      isValid: false,
      errorKey: 'urlRequired'
    };
  }

  const trimmedUrl = url.trim();

  // Check using native URL constructor
  try {
    const urlObj = new URL(trimmedUrl);

    // Only allow http and https protocols
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return {
        isValid: false,
        errorKey: 'urlMissingProtocol'
      };
    }

    // Check if hostname exists
    if (!urlObj.hostname || urlObj.hostname.length === 0) {
      return {
        isValid: false,
        errorKey: 'urlMissingHostname'
      };
    }

    return {
      isValid: true
    };
  } catch {
    return {
      isValid: false,
      errorKey: 'urlInvalidFormat'
    };
  }
}

/** True when the URL carries a user name or a password; false for an unparseable URL. */
export function hasUrlCredentials(url: string): boolean {
  try {
    const urlObj = new URL(url.trim());
    return urlObj.username !== '' || urlObj.password !== '';
  } catch {
    return false;
  }
}
